import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CameraCapture } from "@/components/CameraCapture";
import { ImageLightbox } from "@/components/ImageLightbox";
import { PmcRemoteVerificationPanel } from "@/components/pmc/PmcRemoteVerificationPanel";
import { WorkerAttendanceCalendarDialog } from "@/components/pmc/WorkerAttendanceCalendarDialog";
import {
  enqueueCapture, getQueueLength, flushQueue, isNetworkError,
} from "@/lib/attendanceQueue";
import { Camera, ChevronRight, ScanFace, Clock, CheckCircle2, Lock as LockIcon, ShieldCheck, ShieldAlert, CalendarClock, MapPin, UserCircle2, Upload, ImageIcon, Timer, CalendarDays, RefreshCw, AlertTriangle, Eye } from "lucide-react";
import { toast } from "sonner";
import { getCurrentLocation, fmtTime, fileToBase64, errorMsg } from "@/lib/utils-app";
import { useGpsTrail } from "@/lib/useGpsTrail";

export default function PmcAttendancePage() {
  return (
    <AppShell title="Attendance" subtitle="Tap Mark Absent or Mark Present for each worker">
      <AttendanceFlow role="pmc_supervisor" />
    </AppShell>
  );
}

export function AttendanceFlow({ role }) {
  const { user, deviceStatus } = useAuth();
  const isDeviceApproved = deviceStatus === "approved" || deviceStatus === "not_required" || !deviceStatus;
  const [params, setParams] = useSearchParams();
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState(params.get("park") || "");
  const [employees, setEmployees] = useState([]);
  const [today, setToday] = useState([]);
  const [winStatus, setWinStatus] = useState(null);
  const [visitMap, setVisitMap] = useState({});
  const [absences, setAbsences] = useState({}); // {employee_id: {note}}
  // Employees needing a re-capture today because an earlier capture was
  // rejected (most often by PMC verification). Keyed by employee_id.
  // Backend `/attendance/needs-recapture` already filters by role.
  const [needsRecapture, setNeedsRecapture] = useState({});
  const [absentBusy, setAbsentBusy] = useState({}); // {employee_id: true}
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [photo, setPhoto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [permReason, setPermReason] = useState("");
  const [permBusy, setPermBusy] = useState(false);
  const tickRef = useRef(null);
  const fileInputRef = useRef(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(getQueueLength());
  const [flushing, setFlushing] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState(null);

  // Live ticker for retake countdown timers — updates once per second.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Foreground GPS pings while supervisor is on the attendance screen
  useGpsTrail({ enabled: true, parkIdRef: parkId });

  // load parks for both PMC and Contractor supervisors
  useEffect(() => {
    api.get("/parks").then((r) => {
      setParks(r.data || []);
      const userParks = user?.park_ids || [];
      const paramPark = params.get("park");
      const targetPark = paramPark || (userParks.length > 0 ? userParks[0] : (r.data[0] ? r.data[0].id : ""));
      if (targetPark) {
        setParkId(targetPark);
        if (!paramPark && targetPark) setParams({ park: targetPark });
      }
    });
    // eslint-disable-next-line
  }, []);

  // Set of employee_ids that had an outstanding re-capture obligation as of
  // the last check — null until the first check completes. Used to detect
  // *newly* rejected ("mismatch") captures so we can toast the supervisor
  // instead of silently updating the banner.
  const recapKnownRef = useRef(null);

  const applyRecapItems = useCallback((items) => {
    const recapMap = {};
    for (const it of items) recapMap[it.employee_id] = it;
    setNeedsRecapture(recapMap);
    const known = recapKnownRef.current;
    if (known) {
      for (const it of items) {
        if (!known.has(it.employee_id)) {
          // Newly rejected since our last check — notify the supervisor
          // automatically so they don't have to notice the banner on
          // their own, and point them at the retake option below.
          toast.message("Photo marked mismatch — retake needed", {
            description: `${it.employee_name} (${it.employee_code})${it.rejected_by_name ? ` · rejected by ${it.rejected_by_name}` : ""}${it.rejection_reason ? ` — ${it.rejection_reason}` : ""}`,
            duration: 10000,
          });
        }
      }
    }
    recapKnownRef.current = new Set(items.map((it) => it.employee_id));
  }, []);

  const refreshAll = async () => {
    let currentParkId = parkId;
    if (!currentParkId) {
      const userParks = user?.park_ids || [];
      currentParkId = userParks[0] || (parks[0] ? parks[0].id : "");
      if (currentParkId) setParkId(currentParkId);
    }
    if (!currentParkId) return;
    try {
      const results = await Promise.allSettled([
        api.get("/employees", { params: { park_id: currentParkId } }),
        api.get("/attendance", { params: { park_id: currentParkId, date_from: todayKey(), date_to: todayKey(), captured_by_role: role } }),
        api.get("/attendance/window-status", { params: { park_id: currentParkId } }),
        role === "pmc_supervisor" ? api.get("/park-visit-status/today") : Promise.resolve({ data: { by_park: {} } }),
        api.get("/attendance/absences", { params: { park_id: currentParkId, date: todayKey() } }),
        api.get("/attendance/needs-recapture", { params: { park_id: currentParkId } }).catch(() => ({ data: { items: [] } })),
      ]);
      const [empRes, attRes, wsRes, vsRes, absRes, recapRes] = results;
      if (empRes.status === "fulfilled") setEmployees(empRes.value.data || []);
      if (attRes.status === "fulfilled") setToday(attRes.value.data || []);
      if (wsRes.status === "fulfilled") setWinStatus(wsRes.value.data);
      if (vsRes.status === "fulfilled") setVisitMap(vsRes.value.data?.by_park || {});
      if (absRes.status === "fulfilled") {
        const absMap = {};
        for (const a of (absRes.value.data?.absences || [])) {
          absMap[a.employee_id] = a;
        }
        setAbsences(absMap);
      }
      if (recapRes.status === "fulfilled") {
        applyRecapItems(recapRes.value.data?.items || []);
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refreshAll();
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      api
        .get("/attendance/window-status", { params: { park_id: parkId } })
        .then((r) => setWinStatus(r.data))
        .catch(() => {});
    }, 60_000);
    return () => tickRef.current && clearInterval(tickRef.current);
    // eslint-disable-next-line
  }, [parkId]);

  // Poll needs-recapture on its own faster cadence so a PMC "mismatch"
  // rejection is surfaced (banner + toast) automatically while this page
  // is open, without waiting on the 60s window-status tick or requiring
  // the supervisor to trigger a refresh via some other action.
  useEffect(() => {
    if (!parkId) return;
    let alive = true;
    const id = setInterval(() => {
      api
        .get("/attendance/needs-recapture", { params: { park_id: parkId } })
        .then((r) => alive && applyRecapItems(r.data?.items || []))
        .catch(() => {});
    }, 20_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [parkId, applyRecapItems]);

  // map of {employeeId: {windowId: capture, ...}}
  // For the retake window we need the ORIGINAL capture time, so we also
  // record the earliest `created_at` per (employee, window) — otherwise
  // every retake would extend the 10-minute lock indefinitely.
  const captureMap = useMemo(() => {
    const out = {};
    const firstAt = {};
    for (const c of today) {
      if (c.status === "rejected") continue;
      const wId = c.window_id || "_open";
      out[c.employee_id] = out[c.employee_id] || {};
      firstAt[c.employee_id] = firstAt[c.employee_id] || {};
      const ts = c.created_at;
      const prevFirst = firstAt[c.employee_id][wId];
      // Keep the earliest as `_first_at` on the entry so the row knows when
      // the original attempt happened, irrespective of later retakes.
      const isEarlier = !prevFirst || new Date(ts) < new Date(prevFirst);
      if (isEarlier) firstAt[c.employee_id][wId] = ts;
      out[c.employee_id][wId] = { ...c, _first_at: firstAt[c.employee_id][wId] };
      // Make sure the latest write does not clobber a previously-recorded
      // earlier `_first_at` (when records arrive out of order).
      if (!isEarlier) out[c.employee_id][wId]._first_at = prevFirst;
    }
    return out;
  }, [today]);

  const filtered = employees.filter((e) =>
    `${e.full_name} ${e.employee_code}`.toLowerCase().includes(search.toLowerCase()),
  );

  const activeWindow = winStatus?.active_window || null;
  const windows = winStatus?.windows || [];
  const currentSelfie = winStatus?.current_selfie || null;
  const selfiesByWindow = winStatus?.selfies_by_window || {};
  const hasApprovedPermission = winStatus?.permission?.status === "approved";
  const hasPendingPermission = winStatus?.permission?.status === "pending";
  // Remote verification mode: PMC supervisor verifies attendance photos uploaded by Contractor Supervisor
  const parkVisitStatus = (visitMap[parkId] || {}).status || null;
  const isRemoteVerificationMode = role === "pmc_supervisor" && parkVisitStatus === "not_visited";
  const isGalleryMode = false;

  useEffect(() => {
    if (isRemoteVerificationMode) {
      sessionStorage.setItem("pmc_remote_mode", "1");
    } else {
      sessionStorage.removeItem("pmc_remote_mode");
    }
  }, [isRemoteVerificationMode]);

  // PMC time-slot rule: enforced when PMC marks the park as "Visited"
  const canCapture = !!activeWindow || hasApprovedPermission;
  const selfieReady = !!currentSelfie || (hasApprovedPermission && !!selfiesByWindow["_open"]);
  const needSelfieFirst = canCapture && !selfieReady;

  // ----- Mark / un-mark absent (no photo required) -----
  const markAbsent = async (employee) => {
    if (!employee) return;
    if (!isDeviceApproved) {
      toast.error("This device is not approved for attendance capture. Please submit a selfie unlock request.");
      return;
    }
    if (!window.confirm(`Mark ${employee.full_name} (${employee.employee_code}) as ABSENT for today?\nNo photo will be captured.`)) return;
    setAbsentBusy((b) => ({ ...b, [employee.id]: true }));
    try {
      await api.post("/attendance/mark-absent", {
        employee_id: employee.id,
        date: todayKey(),
      });
      toast.success(`${employee.full_name} marked absent`);
      await refreshAll();
    } catch (err) {
      toast.error(errorMsg(err, "Failed to mark absent"));
    } finally {
      setAbsentBusy((b) => { const n = { ...b }; delete n[employee.id]; return n; });
    }
  };

  const unmarkAbsent = async (employee) => {
    if (!employee) return;
    if (!isDeviceApproved) {
      toast.error("This device is not approved for attendance capture. Please submit a selfie unlock request.");
      return;
    }
    if (!window.confirm(`Remove the absent mark for ${employee.full_name}?`)) return;
    setAbsentBusy((b) => ({ ...b, [employee.id]: true }));
    try {
      await api.delete("/attendance/mark-absent", {
        params: { employee_id: employee.id, date: todayKey() },
      });
      toast.success("Absent mark cleared");
      await refreshAll();
    } catch {
      toast.error("Failed to clear absent mark");
    } finally {
      setAbsentBusy((b) => { const n = { ...b }; delete n[employee.id]; return n; });
    }
  };

  // Selfie capture state
  const [selfiePhoto, setSelfiePhoto] = useState("");
  const [selfieBusy, setSelfieBusy] = useState(false);
  const [selfieMode, setSelfieMode] = useState(false); // explicit selfie panel

  const submitSelfie = async (b64) => {
    const photoB64 = b64 || selfiePhoto;
    if (!photoB64) return;
    if (!isDeviceApproved) {
      toast.error("This device is not approved for attendance capture. Please submit a selfie unlock request.");
      return;
    }
    setSelfieBusy(true);
    try {
      const loc = await getCurrentLocation();
      await api.post("/attendance/supervisor-selfie", {
        park_id: parkId || null,
        photo_b64: photoB64,
        gps_lat: loc?.lat,
        gps_long: loc?.long,
        gps_accuracy_m: loc?.accuracy,
        window_id: activeWindow?.id || null,
      });
      toast.success("Saved");
      setSelfiePhoto("");
      setSelfieMode(false);
      refreshAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 423 && detail?.code === "device_not_approved") {
        toast.error(detail?.message || "This device is not approved. Please request approval from admin.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(detail?.message || "Failed to save");
      }
    } finally {
      setSelfieBusy(false);
    }
  };

  // Auto-save selfie immediately after capture — no separate Submit step.
  const handleSelfiePhoto = (b64) => {
    setSelfiePhoto(b64);
    if (b64) submitSelfie(b64);
  };

  const submit = async (b64) => {
    const photoB64 = b64 || photo;
    if (!selected) return;
    if (!photoB64) return;
    if (!isDeviceApproved) {
      toast.error("This device is not approved for attendance capture. Please submit a selfie unlock request.");
      return;
    }
    setSubmitting(true);
    // GPS is REQUIRED on every worker-attendance capture (PMC and
    // Contractor). Without it the admin's GPS Trail map is useless and
    // we cannot validate that the supervisor was actually at the park.
    // Block submit client-side with a clear toast so the user understands
    // why nothing was saved — instead of letting them hit the backend
    // 400. Network-queued offline submissions also carry GPS this way.
    const loc = await getCurrentLocation().catch(() => null);
    if (!loc || loc.lat == null || loc.long == null) {
      toast.error(
        "Location is required to capture attendance. Please enable GPS / location permission and try again.",
      );
      setSubmitting(false);
      return;
    }
    const payload = {
      employee_id: selected.id,
      photo_b64: photoB64,
      gps_lat: loc.lat,
      gps_long: loc.long,
      gps_accuracy_m: loc.accuracy,
      window_id: isGalleryMode ? null : (activeWindow?.id || null),
      source: isGalleryMode ? "gallery_upload" : "live_camera",
    };
    try {
      const { data } = await api.post("/attendance", payload);
      toast.success("Saved");
      setSelected(null);
      setPhoto("");
      if (data && data.employee_id) {
        setToday((prev) => [
          data,
          ...prev.filter(
            (c) =>
              !(
                c.employee_id === data.employee_id &&
                (c.window_id === data.window_id || (!c.window_id && !data.window_id))
              ) && c.id !== data.id,
          ),
        ]);
      }
      refreshAll();
    } catch (e) {
      if (isNetworkError(e)) {
        const n = enqueueCapture(payload);
        setPendingCount(n);
        const synthRecord = {
          id: `local_${Date.now()}`,
          employee_id: payload.employee_id,
          employee_name: selected.full_name,
          employee_code: selected.employee_code,
          park_id: selected.park_id,
          window_id: payload.window_id,
          window_label: activeWindow?.label || "Open",
          photo_b64: payload.photo_b64,
          created_at: new Date().toISOString(),
          status: "marked",
        };
        setToday((prev) => [synthRecord, ...prev]);
        toast.success(`Queued for sync (${n} pending) — will upload when network is available`);
        setSelected(null);
        setPhoto("");
        return;
      }
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // 423 LOCKED comes in two flavours from the backend:
      //   • {code: "WINDOW_CLOSED"} → window is genuinely closed (need admin permission)
      //   • {code: "device_not_approved"} → device-binding block (need admin to approve THIS device)
      // The old code had `|| true` which made every 423 silently open the
      // "request permission" dialog — including for the device-approval case,
      // confusing the supervisor at Gummidipoondi who saw "window closed"
      // even though Morning 07:00–11:00 IST was clearly open. Treat each
      // code separately and show the correct message.
      if (status === 423 && detail?.code === "device_not_approved") {
        toast.error(
          detail?.message
          || "This device is not approved. Please use the 'Request approval' banner at the top.",
        );
      } else if (status === 423 && (detail?.code === "WINDOW_CLOSED" || !detail?.code)) {
        toast.error("Capture window is closed. Please request permission from admin.");
        setPermOpen(true);
      } else if (status === 428 && detail?.code === "SELFIE_REQUIRED") {
        toast.error("Please capture your own selfie for this window first.");
        setSelfieMode(true);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(detail?.message || "Failed to save");
      }
      setPhoto("");
    } finally {
      setSubmitting(false);
    }
  };

  // Background flusher: tries to upload any queued captures whenever the
  // browser regains network, when this component mounts, and every 30 s
  // while there's anything pending.
  const tryFlush = useCallback(async () => {
    if (getQueueLength() === 0 || flushing) {
      setPendingCount(getQueueLength());
      return;
    }
    setFlushing(true);
    try {
      const res = await flushQueue((p) => api.post("/attendance", p));
      setPendingCount(res.remaining);
      if (res.uploaded > 0) {
        toast.success(`${res.uploaded} queued capture${res.uploaded === 1 ? "" : "s"} synced`);
        refreshAll();
      }
      if (res.failed > 0) {
        toast.error(`${res.failed} queued capture${res.failed === 1 ? "" : "s"} dropped (server rejected)`);
      }
    } finally {
      setFlushing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushing]);

  useEffect(() => {
    tryFlush();
    const onOnline = () => tryFlush();
    window.addEventListener("online", onOnline);
    const id = setInterval(() => {
      if (navigator.onLine && getQueueLength() > 0) tryFlush();
    }, 30000);
    return () => {
      window.removeEventListener("online", onOnline);
      clearInterval(id);
    };
  }, [tryFlush]);

  // Auto-save worker photo immediately after capture — no Submit click.
  const handleWorkerPhoto = (b64) => {
    setPhoto(b64);
    if (b64) submit(b64);
  };

  const onGalleryFile = async (e) => {
    const f = e.target?.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const b64 = await fileToBase64(f);
      setPhoto(b64);
      // Auto-save on gallery pick too — no separate Submit click.
      if (selected) submit(b64);
    } catch {
      toast.error("Failed to read photo");
    }
  };

  const requestPermission = async () => {
    if (!parkId) return;
    setPermBusy(true);
    try {
      await api.post("/attendance/permissions/request", {
        park_id: parkId,
        date: todayKey(),
        window_id: null,
        reason: permReason.trim() || "",
      });
      toast.success("Permission requested. Admin will review shortly.");
      setPermOpen(false);
      setPermReason("");
      refreshAll();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to request permission"));
    } finally {
      setPermBusy(false);
    }
  };

  const handleSwitchToVisited = async () => {
    if (!parkId) return;
    try {
      const loc = await getCurrentLocation().catch(() => null);
      await api.post("/park-visit-status", {
        park_id: parkId,
        status: "visited",
        gps_lat: loc?.lat || null,
        gps_long: loc?.long || null,
      });
      toast.success("Switched to Park Visited — Live camera capture mode enabled");
      await refreshAll();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to update park visit status"));
    }
  };

  const handleSwitchToNotVisited = async () => {
    if (!parkId) return;
    if (!window.confirm("Switch to Remote Verification Mode? (Mark park as Not Visited)")) return;
    try {
      await api.post("/park-visit-status", {
        park_id: parkId,
        status: "not_visited",
      });
      toast.success("Switched to Remote Verification Mode");
      await refreshAll();
    } catch (e) {
      toast.error(errorMsg(e, "Failed to update park visit status"));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Worker attendance calendar — PMC supervisors only */}
      {role === "pmc_supervisor" && (
        <>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setCalendarOpen(true)}
              data-testid="worker-cal-open"
            >
              <CalendarDays className="h-4 w-4" />
              Attendance calendar
            </Button>
          </div>
          <WorkerAttendanceCalendarDialog
            open={calendarOpen}
            onClose={() => setCalendarOpen(false)}
          />
        </>
      )}


      {/* Remote Verification Panel for Not Visited parks — PMC supervisor verifies contractor uploads */}
      {isRemoteVerificationMode ? (
        <PmcRemoteVerificationPanel
          parkId={parkId}
          onChange={refreshAll}
          onSwitchToVisited={handleSwitchToVisited}
        />
      ) : (
        <>
          {role === "pmc_supervisor" && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-1.5 text-xs text-emerald-900">
              <div className="flex items-center gap-1.5 font-medium">
                <Camera className="h-3.5 w-3.5 text-emerald-600" />
                Mode: Live Camera Photo Capture (Park Visited)
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                onClick={handleSwitchToNotVisited}
                data-testid="attendance-switch-not-visited-btn"
              >
                <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                Switch to Remote Verification Mode
              </Button>
            </div>
          )}

          {/* Window banner */}
          <WindowBanner
            windows={windows}
            active={activeWindow}
            permission={winStatus?.permission}
            onRequest={() => setPermOpen(true)}
          />

          {/* Selfie status banner */}
          {(selfieReady || canCapture) && (
            <SelfieBanner
              selfie={currentSelfie}
              ready={selfieReady}
              activeWindow={activeWindow}
              onCapture={() => setSelfieMode(true)}
              needSelfieFirst={needSelfieFirst}
            />
          )}

          {/* Park / search */}
          <Card>
            <CardContent className="grid gap-2 p-3 sm:grid-cols-2">
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="attendance-fixed-park">
                <MapPin className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate">
                  {parks.find((p) => p.id === parkId)?.name || "Loading park…"}
                </span>
              </div>
              <Input
                placeholder="Search employee…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="attendance-search-input"
              />
            </CardContent>
          </Card>

      {/* Re-capture banner — visible when one or more workers in the
          current park had their earlier capture rejected today. Encourages
          the supervisor to retake those workers' attendance even if the
          regular Morning/Afternoon window has already closed. */}
      {(() => {
        const items = Object.values(needsRecapture).filter(
          (it) => !it || !it.employee_id ? false : true,
        );
        // Drop any worker who already has a non-rejected capture (defence
        // in depth — backend already filters this).
        const pending = items.filter((it) => {
          const caps = captureMap[it.employee_id] || {};
          return Object.keys(caps).length === 0;
        });
        if (!pending.length) return null;
        return (
          <Card className="border-amber-300 bg-amber-50" data-testid="attendance-recap-banner">
            <CardContent className="flex flex-wrap items-start gap-2 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  Re-capture needed for {pending.length} worker{pending.length === 1 ? "" : "s"}
                </div>
                <div className="mt-0.5 text-xs">
                  {pending.slice(0, 4).map((it) => (
                    <span
                      key={it.employee_id}
                      className="mr-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white/60 px-2 py-0.5"
                      data-testid={`attendance-recap-chip-${it.employee_code}`}
                      title={
                        it.rejection_reason
                          ? `Reason: ${it.rejection_reason}`
                          : "Earlier capture was rejected"
                      }
                    >
                      <RefreshCw className="h-3 w-3" />
                      {it.employee_name} ({it.employee_code})
                    </span>
                  ))}
                  {pending.length > 4 ? (
                    <span className="text-[11px] italic text-amber-700">
                      … and {pending.length - 4} more
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 text-[11px] text-amber-700">
                  These workers are unlocked for re-capture even if the regular slot is closed.
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {selfieMode || needSelfieFirst ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserCircle2 className="h-5 w-5 text-primary" />
              Your selfie {activeWindow ? `· ${activeWindow.label}` : ""}
            </CardTitle>
            <div className="text-xs text-muted-foreground">
              Capture your own selfie to start attendance for this window. Date &amp; time are stamped automatically.
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <CameraCapture
              captured={selfiePhoto}
              onCapture={handleSelfiePhoto}
              onClear={() => setSelfiePhoto("")}
              facingMode="user"
              showFlip={true}
              testIdPrefix="supervisor-selfie"
              overlayDateTime={true}
              imageMaxWidth={1024}
              imageQuality={0.7}
            />
            {selfieBusy && <div className="text-center text-xs text-muted-foreground">Saving…</div>}
            {!needSelfieFirst && (
              <Button
                variant="outline"
                onClick={() => {
                  setSelfieMode(false);
                  setSelfiePhoto("");
                }}
                disabled={selfieBusy}
                data-testid="supervisor-selfie-cancel"
              >
                Back
              </Button>
            )}
          </CardContent>
        </Card>
      ) : !selected ? (
        <div className="grid gap-2" data-testid="attendance-employee-list">
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-sm text-muted-foreground">
                No employees deployed for this park.
              </CardContent>
            </Card>
          ) : null}
          {filtered.map((e) => {
            const caps = captureMap[e.id] || {};
            const captureList = Object.values(caps);
            // Sorted list of TODAY's captures for this worker — used to
            // render a small chip strip on every row so the supervisor can
            // always see what they've already captured today, regardless
            // of whether the current slot is still active. This is what
            // prevents the "page refreshed, my work is gone!" UX bug.
            const captureListSorted = [...captureList].sort(
              (a, b) => new Date(a.created_at) - new Date(b.created_at),
            );
            const latestCap = captureListSorted.length > 0 ? captureListSorted[captureListSorted.length - 1] : null;
            // PER-WINDOW done detection — when a slot (e.g., Afternoon) opens,
            // the morning capture must NOT lock the row out of the new slot.
            // We only treat the worker as "done" if a capture exists with the
            // currently-active window id (or, when no window is active, when
            // a gallery upload exists).
            //
            // Special case: when Admin Override is granted (no regular slot
            // active, but `hasApprovedPermission` is true), the supervisor is
            // operating in the "_open" pseudo-window. Any *new* captures get
            // stored with `window_id=null` → keyed as `_open`. So the active
            // window id during admin override must be `_open`, otherwise
            // prior captures appear "missing" and Mark Present stays falsely
            // active for already-attended workers (the refresh-after-override
            // bug from Hosur on 17-Jun).
            const activeWid =
              activeWindow?.id ||
              (isGalleryMode || hasApprovedPermission ? "_open" : null);
            const currentWindowCap = (activeWid ? caps[activeWid] : null) || caps["_open"] || null;
            const firstCap = currentWindowCap || captureList[0];
            const doneInWindow = !!currentWindowCap;
            // "Captured today" — any non-rejected capture from any slot/
            // window/gallery, including the historical Morning + Afternoon
            // rows even when the supervisor reopens the page after both
            // windows have closed.
            const presentToday = captureList.length > 0;
            // Show the green "Done" badge whenever the worker is already
            // attended for today *in the current operating context*. During
            // a regular slot that means a capture exists in this slot. When
            // operating under Admin Override / outside regular slots, ANY
            // capture today (Morning, Afternoon, Open) counts — supervisors
            // should not be allowed to "Mark Present" a worker who has
            // already been recorded today.
            // True whenever there is no "primary" slot open — i.e. no
            // regular Morning/Afternoon window AND not in gallery upload
            // mode. Used to fall back the "Done" treatment onto historical
            // Morning/Afternoon captures so the supervisor doesn't see
            // already-attended workers as needing a Mark Present.
            //
            // Under admin override, a worker counts as "fully done" only
            // when every configured attendance window has its own capture
            // (e.g. Morning + Afternoon for a 2-slot park). If they're
            // missing a slot (e.g. only Morning so far), the supervisor
            // must be able to fill the gap with Mark Present — even
            // though they technically have *some* capture today. The
            // page-reloaded-after-closed-windows (no override) case keeps
            // the simpler "any capture = Done" treatment so refreshed
            // pages don't re-prompt the supervisor.
            const outsideRegularSlot = !activeWindow && !isGalleryMode;
            let allConfiguredWindowsDone = false;
            if (outsideRegularSlot && hasApprovedPermission && windows.length) {
              allConfiguredWindowsDone = windows.every((w) => !!caps[w.id]);
            }
            const doneToday = doneInWindow
              || (outsideRegularSlot && presentToday && !hasApprovedPermission)
              || (outsideRegularSlot && hasApprovedPermission && allConfiguredWindowsDone);
            // Re-capture obligation — this worker had an earlier capture
            // today that was rejected (typically by PMC). Until they have
            // a fresh non-rejected capture, we want them to be capturable
            // even when the regular window has closed.
            const recapInfo = needsRecapture[e.id] || null;
            const needsRecap = !!recapInfo && !presentToday;
            const isAbsent = !!absences[e.id];
            const myAbsence = absences[e.id];
            // Was this absence set by the currently-logged-in supervisor? Only
            // the same supervisor can clear their own absent mark — and only
            // within the 10-minute undo window.
            const ABSENT_UNDO_MS = 10 * 60 * 1000;
            const absentCreatedAt = myAbsence?.created_at
              ? new Date(myAbsence.created_at).getTime() : null;
            const absentSecondsLeft = absentCreatedAt
              ? Math.max(0, Math.floor((absentCreatedAt + ABSENT_UNDO_MS - nowMs) / 1000)) : 0;
            const canClearAbsence =
              isAbsent && myAbsence?.set_by === user?.id && absentSecondsLeft > 0;
            // Retake window: 10 minutes since the FIRST capture (not the latest retake)
            const firstCapTime = firstCap?._first_at || firstCap?.created_at;
            const capCreated = firstCapTime ? new Date(firstCapTime) : null;
            const retakeSecondsLeft = capCreated && doneInWindow
              ? Math.max(0, Math.floor((capCreated.getTime() + 10 * 60 * 1000 - nowMs) / 1000))
              : 0;
            // Allowed to (re)open this row when:
            //  - no capture yet in current window AND can capture, OR
            //  - already captured in current window but within the 10-min retake window
            //  - this employee has an outstanding re-capture obligation
            //    (PMC rejected an earlier capture today) — bypasses the
            //    closed-window block at the BACKEND too.
            //
            // During admin override / outside any regular slot we honour the
            // same `doneToday` flag (which already collapses any Morning /
            // Afternoon / Open capture into "done") so the supervisor cannot
            // accidentally re-capture an already-attended worker.
            const canOpen = !isAbsent && (
              needsRecap || (
                isGalleryMode
                  ? !doneToday || retakeSecondsLeft > 0
                  : (!doneToday && canCapture) || retakeSecondsLeft > 0
              )
            );
            const disabled = !canOpen;
            const busyForRow = !!absentBusy[e.id];
            return (
              <div
                key={e.id}
                className="text-left"
                data-testid={`attendance-emp-${e.employee_code}`}
              >
                <Card className={`hover:bg-secondary/40 ${(disabled && !isAbsent) ? "opacity-70" : ""}`}>
                  <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      {/* Employee photo display (Captured photo if available, otherwise Reference photo) */}
                      {(() => {
                        const displayPhoto = latestCap?.photo_b64 || e.reference_photo_b64;
                        const isCaptured = !!latestCap?.photo_b64;
                        const isRef = !isCaptured && !!e.reference_photo_b64;
                        const photoSrc = displayPhoto
                          ? (displayPhoto.startsWith("data:") ? displayPhoto : `data:image/jpeg;base64,${displayPhoto}`)
                          : null;

                        return (
                          <div className="flex shrink-0 items-center gap-1.5">
                            <div
                              className={[
                                "group relative grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary transition-all",
                                photoSrc ? "cursor-pointer hover:scale-105 hover:shadow-md" : "",
                                isCaptured ? "border-2 border-emerald-500" : "border border-border",
                              ].join(" ")}
                              title={
                                isCaptured
                                  ? "Captured attendance photo — tap to view full size"
                                  : isRef
                                  ? "Reference photo — tap to view full size"
                                  : "No photo available"
                              }
                              onClick={(ev) => {
                                if (!photoSrc) return;
                                ev.stopPropagation();
                                setLightboxSrc(photoSrc);
                                setLightboxCaption(
                                  isCaptured
                                    ? `Captured Photo: ${e.full_name} (${e.employee_code}) · ${latestCap.window_label || "Open"} (${fmtTime(latestCap.created_at)})`
                                    : `Reference Photo: ${e.full_name} (${e.employee_code})`
                                );
                              }}
                              data-testid={`attendance-emp-${e.employee_code}-ref`}
                            >
                              {photoSrc ? (
                                <>
                                  <img
                                    src={photoSrc}
                                    alt={isCaptured ? `Captured photo of ${e.full_name}` : `Reference photo of ${e.full_name}`}
                                    className="h-full w-full object-cover"
                                    data-testid={`attendance-emp-${e.employee_code}-${isCaptured ? "captured" : "ref"}`}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                                    <Eye className="h-4 w-4 text-white drop-shadow" />
                                  </div>
                                </>
                              ) : (
                                <ScanFace className="h-5 w-5 text-muted-foreground" />
                              )}
                              <span
                                className={[
                                  "absolute bottom-0 inset-x-0 text-[8px] text-white text-center py-0.5 font-bold leading-none uppercase",
                                  isCaptured ? "bg-emerald-700/90" : "bg-black/60",
                                ].join(" ")}
                              >
                                {isCaptured ? "TAKEN" : "REF"}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium truncate">{e.full_name}</div>
                          {doneToday && (
                            <Badge
                              className="bg-emerald-600 hover:bg-emerald-600 text-white"
                              data-testid={`attendance-emp-${e.employee_code}-captured`}
                            >
                              <CheckCircle2 className="mr-1 h-3 w-3" /> Done
                            </Badge>
                          )}
                          {!doneToday && presentToday && (
                            <Badge
                              variant="outline"
                              className="border-emerald-300 bg-emerald-50 text-emerald-800"
                              data-testid={`attendance-emp-${e.employee_code}-present-today`}
                              title="Captured earlier today in a different slot"
                            >
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              Present today
                            </Badge>
                          )}
                          {isAbsent && (
                            <Badge
                              variant="outline"
                              className="border-rose-300 bg-rose-50 text-rose-700"
                              data-testid={`attendance-emp-${e.employee_code}-absent`}
                            >
                              Absent
                            </Badge>
                          )}
                          {needsRecap && (
                            <Badge
                              className="bg-amber-500 text-white hover:bg-amber-500"
                              data-testid={`attendance-emp-${e.employee_code}-recapture`}
                              title={
                                recapInfo?.rejection_reason
                                  ? `PMC rejected${recapInfo.rejected_by_name ? ` by ${recapInfo.rejected_by_name}` : ""}: ${recapInfo.rejection_reason}`
                                  : "PMC rejected the earlier capture — please re-capture"
                              }
                            >
                              <RefreshCw className="mr-1 h-3 w-3" />
                              Re-capture needed
                            </Badge>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground truncate">
                          <span className="font-mono">{e.employee_code}</span>
                          {e.designation ? <> &middot; {e.designation}</> : null}
                          {isAbsent && myAbsence?.set_by_name ? (
                            <> &middot; marked by {myAbsence.set_by_name}</>
                          ) : null}
                        </div>
                        {/* Today's capture strip — always rendered so the
                            supervisor can see exactly what's already been
                            recorded today, even after a page refresh outside
                            any active window. */}
                        {captureListSorted.length > 0 && (
                          <div
                            className="mt-1 flex flex-wrap items-center gap-1"
                            data-testid={`attendance-emp-${e.employee_code}-today-strip`}
                          >
                            {captureListSorted.map((c) => {
                              const isOpen = !c.window_label;
                              return (
                                <span
                                  key={c.id || c.created_at}
                                  className={[
                                    "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px]",
                                    c.photo_b64 ? "cursor-pointer hover:shadow-sm" : "",
                                    isOpen
                                      ? "border-amber-300 bg-amber-50 text-amber-900"
                                      : "border-emerald-300 bg-emerald-50 text-emerald-900",
                                  ].join(" ")}
                                  title={c.photo_b64 ? "Click to view photo taken" : (isOpen ? "Captured outside any configured slot" : `Captured in ${c.window_label}`)}
                                  onClick={(ev) => {
                                    if (c.photo_b64) {
                                      ev.stopPropagation();
                                      setLightboxSrc(c.photo_b64.startsWith("data:") ? c.photo_b64 : `data:image/jpeg;base64,${c.photo_b64}`);
                                      setLightboxCaption(`Captured Photo: ${e.full_name} (${e.employee_code}) · ${c.window_label || "Open"} (${fmtTime(c.created_at)})`);
                                    }
                                  }}
                                  data-testid={`attendance-emp-${e.employee_code}-cap-${c.window_id || "open"}`}
                                >
                                  <Clock className="h-2.5 w-2.5" />
                                  {(c.window_label || "Open")} · {fmtTime(c.created_at)}
                                  {c.photo_b64 && <Eye className="h-2.5 w-2.5 ml-0.5 text-emerald-700" />}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Action area — stacks under info on mobile, sits inline on desktop */}
                    <div className="flex shrink-0 items-center gap-1.5 sm:ml-auto">
                      {!isAbsent && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 flex-1 px-2 text-[12px] text-rose-700 hover:bg-rose-50 sm:flex-none"
                          onClick={(ev) => { ev.stopPropagation(); markAbsent(e); }}
                          disabled={busyForRow}
                          data-testid={`attendance-emp-${e.employee_code}-mark-absent`}
                          title={doneToday && retakeSecondsLeft > 0
                            ? `Edit window closes in ${Math.floor(retakeSecondsLeft / 60)}m ${retakeSecondsLeft % 60}s`
                            : "Mark this worker absent (last action wins)"}
                        >
                          {doneToday && retakeSecondsLeft > 0
                            ? `Flip to Absent · ${Math.floor(retakeSecondsLeft / 60)}:${String(retakeSecondsLeft % 60).padStart(2, "0")}`
                            : doneToday
                              ? "Flip to Absent"
                              : "Mark Absent"}
                        </Button>
                      )}
                      {isAbsent && canClearAbsence && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-9 flex-1 px-2 text-[12px] sm:flex-none"
                          onClick={(ev) => { ev.stopPropagation(); unmarkAbsent(e); }}
                          disabled={busyForRow}
                          data-testid={`attendance-emp-${e.employee_code}-unmark-absent`}
                          title={`Undo window closes in ${Math.floor(absentSecondsLeft / 60)}m ${absentSecondsLeft % 60}s`}
                        >
                          Undo · {Math.floor(absentSecondsLeft / 60)}:{String(absentSecondsLeft % 60).padStart(2, "0")}
                        </Button>
                      )}
                      {!isAbsent && (
                        <Button
                          size="sm"
                          className="h-9 flex-1 px-3 text-[12px] bg-emerald-600 hover:bg-emerald-700 text-white sm:flex-none"
                          onClick={() => {
                            if (disabled) return;
                            setSelected(e);
                            setPhoto("");
                          }}
                          disabled={disabled}
                          aria-label="Mark Present"
                          data-testid={`attendance-emp-${e.employee_code}-open`}
                        >
                          <Camera className="mr-1 h-3.5 w-3.5" />
                          {doneToday && retakeSecondsLeft > 0
                            ? `Retake · ${Math.floor(retakeSecondsLeft / 60)}:${String(retakeSecondsLeft % 60).padStart(2, "0")}`
                            : doneToday
                              ? "Done"
                              : "Mark Present"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base truncate">{isGalleryMode ? "Upload photo" : "Capture"} &mdash; {selected.full_name}</CardTitle>
            <div className="text-[11px] text-muted-foreground truncate">
              <span className="font-mono">{selected.employee_code}</span>
              {selected.designation ? <> &middot; {selected.designation}</> : null}
              {isGalleryMode ? (
                <> &middot; <span className="text-amber-700">Gallery upload</span></>
              ) : activeWindow ? (
                <>
                  {" · "}
                  <span className="text-primary">{activeWindow.label}</span>
                </>
              ) : hasApprovedPermission ? (
                <> &middot; <span className="text-amber-600">Override</span></>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {/* Compact reference thumbnail — single tap to enlarge.
                Kept tiny so the camera takes the full screen height on mobile. */}
            {selected.reference_photo_b64 ? (
              <div
                className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
                data-testid="reference-photo-block"
              >
                <img
                  src={selected.reference_photo_b64.startsWith("data:") ? selected.reference_photo_b64 : `data:image/jpeg;base64,${selected.reference_photo_b64}`}
                  alt={`Reference of ${selected.full_name}`}
                  className="h-10 w-10 shrink-0 rounded-md border object-cover"
                  data-testid="reference-photo-img"
                />
                <div className="text-[11px] text-muted-foreground">Reference photo</div>
              </div>
            ) : null}

            {isGalleryMode ? (
              <GalleryPicker
                photo={photo}
                onClear={() => setPhoto("")}
                onPick={() => fileInputRef.current?.click()}
                submitting={submitting}
              />
            ) : (
              <CameraCapture
                captured={photo}
                onCapture={handleWorkerPhoto}
                onClear={() => setPhoto("")}
                testIdPrefix="attendance-camera"
                overlayDateTime={true}
                imageMaxWidth={1280}
                imageQuality={0.7}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onGalleryFile}
              data-testid="attendance-gallery-input"
            />
            {submitting && <div className="text-center text-xs text-muted-foreground">Saving…</div>}
            <Button
              variant="outline"
              className="h-11"
              onClick={() => {
                setSelected(null);
                setPhoto("");
              }}
              disabled={submitting}
              data-testid="attendance-cancel-button"
            >
              Back
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Request permission dialog */}
      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request permission to capture</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
              Capture windows are closed right now. Submit a request to admin and capture once approved
              for{" "}
              <b>{(parks.find((p) => p.id === parkId) || {}).name}</b>.
              <span className="block mt-1">A reason is optional.</span>
            </div>
            <Textarea
              value={permReason}
              onChange={(e) => setPermReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={3}
              data-testid="attendance-permission-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermOpen(false)} disabled={permBusy}>
              Cancel
            </Button>
            <Button onClick={requestPermission} disabled={permBusy} data-testid="attendance-permission-submit">
              {permBusy ? "Sending…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image lightbox modal for viewing captured employee photos */}
      {lightboxSrc ? (
        <ImageLightbox
          src={lightboxSrc}
          caption={lightboxCaption}
          onClose={() => {
            setLightboxSrc(null);
            setLightboxCaption(null);
          }}
        />
      ) : null}
        </>
      )}
    </div>
  );
}

function todayKey() {
  // IST today key
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function WindowBanner({ windows, active, permission, onRequest }) {
  if (!windows || windows.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
          <CalendarClock className="h-4 w-4" />
          No attendance windows are configured. Ask admin to set them in Settings.
        </CardContent>
      </Card>
    );
  }
  if (active) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/60">
        <CardContent className="flex flex-wrap items-center gap-3 p-3 text-sm">
          <div className="flex items-center gap-2 text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-medium" data-testid="window-status-active">Open: {active.label}</span>
          </div>
          <div className="text-xs text-emerald-700/80">
            {active.start_time}–{active.end_time} IST
          </div>
          <div className="ml-auto flex flex-wrap gap-1.5">
            {windows.map((w) => (
              <Badge
                key={w.id}
                variant={w.id === active.id ? "default" : "secondary"}
                className={w.id === active.id ? "bg-emerald-600 hover:bg-emerald-600 text-white" : "opacity-70"}
              >
                <Clock className="mr-1 h-3 w-3" />
                {w.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  // Window closed
  if (permission?.status === "approved") {
    const expIso = permission?.expires_at;
    let untilLabel = null;
    if (expIso) {
      try {
        const exp = new Date(expIso);
        untilLabel = exp.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" });
      } catch { untilLabel = null; }
    }
    return (
      <Card className="border-amber-200 bg-amber-50/60">
        <CardContent className="flex flex-wrap items-center gap-2 p-3 text-sm text-amber-800">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-medium" data-testid="window-status-override">Admin override active</span>
          <span className="text-xs">
            {untilLabel
              ? `— valid until ${untilLabel} IST. Capture for as many workers as you need within this window.`
              : "— you can capture multiple workers within the granted window."}
          </span>
        </CardContent>
      </Card>
    );
  }
  if (permission?.status === "pending") {
    return (
      <Card className="border-slate-200 bg-secondary/40">
        <CardContent className="flex flex-wrap items-center gap-2 p-3 text-sm">
          <Clock className="h-4 w-4 text-slate-700" />
          <span className="font-medium" data-testid="window-status-pending">Permission requested</span>
          <span className="text-xs text-muted-foreground">Awaiting admin approval.</span>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-rose-200 bg-rose-50/60">
      <CardContent className="flex flex-wrap items-center gap-2 p-3 text-sm">
        <LockIcon className="h-4 w-4 text-rose-700" />
        <span className="font-medium text-rose-800" data-testid="window-status-closed">Capture window is closed</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {windows.map((w) => (
            <Badge key={w.id} variant="secondary" className="opacity-70">
              <Clock className="mr-1 h-3 w-3" />
              {w.label} {w.start_time}–{w.end_time}
            </Badge>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={onRequest} data-testid="attendance-request-permission-button">
          <ShieldAlert className="mr-1.5 h-3.5 w-3.5" /> Request permission
        </Button>
      </CardContent>
    </Card>
  );
}

function SelfieBanner({ selfie, ready, activeWindow, onCapture, needSelfieFirst }) {
  if (!activeWindow && !ready) return null;
  if (ready && selfie) {
    // Confirmation only — no timestamp, no thumbnail, no window label.
    return (
      <Card className="border-emerald-200 bg-emerald-50/60">
        <CardContent className="flex flex-wrap items-center gap-2 p-3 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
          <span className="font-medium text-emerald-800">Ready to capture workers</span>
        </CardContent>
      </Card>
    );
  }
  if (needSelfieFirst) {
    return (
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex flex-wrap items-center gap-3 p-3 text-sm">
          <UserCircle2 className="h-4 w-4 text-primary" />
          <span className="font-medium">Capture your selfie to start this window</span>
          <span className="text-xs text-muted-foreground">— required before capturing workers.</span>
          <Button size="sm" className="ml-auto" onClick={onCapture} data-testid="supervisor-selfie-open">
            <Camera className="mr-1.5 h-3.5 w-3.5" /> Take selfie
          </Button>
        </CardContent>
      </Card>
    );
  }
  return null;
}


function GalleryPicker({ photo, onClear, onPick, submitting }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border bg-muted">
        {photo ? (
          <img src={photo} alt="upload preview" className="absolute inset-0 h-full w-full object-cover" data-testid="gallery-preview-img" />
        ) : (
          <div className="absolute inset-0 grid place-items-center p-6 text-center text-xs text-muted-foreground">
            <div>
              <ImageIcon className="mx-auto mb-2 h-8 w-8" />
              <div>Pick a photo for this worker from your gallery.</div>
            </div>
          </div>
        )}
      </div>
      {!photo && (
        <Button onClick={onPick} disabled={submitting} data-testid="gallery-pick-button">
          <Upload className="mr-2 h-4 w-4" /> Choose photo
        </Button>
      )}
    </div>
  );
}

